import axios from 'axios';
import { isDev, isStaging, isLocalDev } from '../utils/env';

const http = axios.create({ timeout: 15000 });

// Request deduplication: store in-flight promises by method+url+body
const inflight = new Map<string, Promise<any>>();

function getRequestKey(config: any) {
  const { method, url, data } = config;
  return `${method || 'get'}:${url || ''}:${JSON.stringify(data || '')}`;
}

http.interceptors.request.use((config) => {
  (config as any).meta = { start: performance.now(), retryCount: 0 };
  return config;
});

// Dedupe by key and share the same promise
const origRequest = http.request.bind(http);
http.request = function dedupedRequest(config: any) {
  const key = getRequestKey(config);
  if (inflight.has(key)) {
    return inflight.get(key)!;
  }
  const promise = origRequest(config).finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
};

http.interceptors.response.use(
  (response) => {
    if ((isDev || isStaging || isLocalDev) && (response.config as any).meta) {
      const ms = (performance.now() - (response.config as any).meta.start).toFixed(0);
      // eslint-disable-next-line no-console
      console.info(`[api] ${response.status} ${response.config.url} ${ms}ms`);
    }
    return response;
  },
  async (error) => {
    const config = error.config || {};
    // Central error mapping
    let mappedError = error;

    if (error.response) {
      const status = error.response.status;
      if (status === 401) mappedError.message = 'Unauthorized';
      if (status === 403) mappedError.message = 'Forbidden';
      if (status === 404) mappedError.message = 'Not found';
      if (status === 429) mappedError.message = 'Rate limited';
      if (status >= 500) mappedError.message = 'Server error';
    } else {
      mappedError.message = 'Network error';
    }

    // Automatic retry for network errors and 429/503 backoff
    const shouldRetry = !config._retry && (!error.response || [429, 503].includes(error.response.status));

    if (shouldRetry && (config as any).meta.retryCount < 3) {
      (config as any).meta.retryCount++;
      config._retry = true;
      // Exponential backoff for 429/503
      let delay = 500 * Math.pow(2, (config as any).meta.retryCount);
      if (error.response && error.response.status === 429 && error.response.headers['retry-after']) {
        delay = parseInt(error.response.headers['retry-after'], 10) * 1000 || delay;
      }
      await new Promise((res) => setTimeout(res, delay));
      return http(config);
    }

    if (isDev || isStaging || isLocalDev) {
      const status = error?.response?.status;
      const url = error?.config?.url;
      // eslint-disable-next-line no-console
      console.warn(`[api] error ${status || 'network'} ${url} - ${mappedError.message}`);
    }

    return Promise.reject(mappedError);
  }
);

export default http;
